import React, { useState, useCallback, useEffect, useRef } from 'react';
import i18n from 'i18n';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { get, isEmpty, noop } from 'lodash';
import moment from 'moment';
import QRCode from 'react-qr-code';
import apiCubic from 'config/api-cubic';
import requester from 'utils/requester';
import scaleSize from 'utils/scale-size';
import innerAnalyticsVolia from 'helpers/innerAnalyticsVolia';
import Platform from 'platform';
import { backToPageThatCausedAuth } from 'actions/app';
import { showLoader, hideLoader } from 'actions/loader';
import { INIT_UI_LANGUAGE } from 'constants/languages';
import { MODAL_TYPES } from 'constants/modal-types';
import { hideModal, showModal } from 'actions/modal';
import { selectUILanguage } from 'reducers/app/selectors';
import { checkLoginData, loginUser } from 'actions/profile';
import useTimer from 'hooks/useTimer';
import RetryIcon from 'components/Icons/retry-code';
import {
  StyledQRTitle,
  StyledQRBlock,
  QRDescription,
  QRCodeWrapper,
  RetryTextWrapper,
  QRLink,
  StyledCodeBlock,
  CodeTitle,
  UserCode,
  StyledActionSection,
  StyledButton,
  StyledTimer,
} from './QRPageStyledComponents';
import useOnSuccessLogin from 'hooks/useOnSuccessLogin';

const SECTION_NAME = 'RENEW_CODE';

const i18nScope = 'authorizationLoginQRPage';

const EXPIRED_CODE = 'expired_code';

const formatCode = (code) => String(code).replace(/(\S{2})/g, '$1 ');

const timeFormatter = (ms) => {
  return ms && moment(ms).format('m:ss');
};

const RetryBlock = () => {
  return (
    <RetryTextWrapper>
      <RetryIcon />
      <p>{i18n.t(`${i18nScope}.retryText`)}</p>
    </RetryTextWrapper>
  );
};

const AuthorizationLoginQRPage = () => {
  const language = useSelector(selectUILanguage);
  const dispatch = useDispatch();
  const history = useHistory();
  const intervalRef = useRef(null);
  const [QRCodeConfig, setQRCodeConfig] = useState({});
  const [isCodeExpired, setIsCodeExpired] = useState(false);
  const expiresInMs = Number((QRCodeConfig || {}).expiresIn) * 1000;
  const timer = useTimer(expiresInMs, [QRCodeConfig.userCode]);

  const resolveSuccess = useOnSuccessLogin();

  const requestQRCode = useCallback(
    async ({ fromButtonClick = false } = {}) => {
      try {
        dispatch(showLoader());

        if (fromButtonClick) {
          innerAnalyticsVolia.postEvent({
            eventGroup: 'external authorization screen',
            eventName: 'click get a new code',
          });
        }

        const { data } = await requester({
          method: 'post',
          url: `${apiCubic.host}${apiCubic.endpoints.getQrCode}`,
          appendParams: false,
          extendHeadersWithSession: false,
          headers: { Authorization: `Basic ${apiCubic.getAuthToken()}` },
          data: {
            uuId: Platform.getUID(),
            deviceDescription: `${Platform.getBrand()} ${Platform.getModelName()}`,
            language,
          },
        });

        setQRCodeConfig(data.payload);

        setIsCodeExpired(false);
      } catch (error) {
        console.error(error);
      } finally {
        dispatch(hideLoader());
      }
    },
    [dispatch, language],
  );

  useEffect(() => {
    if (isEmpty(QRCodeConfig) || isCodeExpired) return;

    const smartTvLogin = async () => {
      const searchParams = new URLSearchParams(history.location.search);
      const errorModal = { returnFocusTo: `@${SECTION_NAME}` };

      const { data, error } = await requester({
        method: 'post',
        url: `${apiCubic.host}${apiCubic.endpoints.smartTVLogin}`,
        appendParams: false,
        extendHeadersWithSession: false,
        headers: { Authorization: `Basic ${apiCubic.getAuthToken()}` },
        data: { deviceCode: QRCodeConfig.deviceCode, language },
      });

      const ottCustomerId = get(data, ['payload', 'ottCustomerId']);
      const errorCode = get(error, ['code']);

      const onSuccess = async (user) => {
        innerAnalyticsVolia.postEvent({
          eventGroup: 'external authorization screen',
          eventName: 'successful login',
        });

        const authToken = apiCubic.getAuthToken();
        const { contractNumber } = user;

        try {
          const response = await requester({
            method: 'post',
            url: `${apiCubic.host}${apiCubic.endpoints.checkContract}`,
            appendParams: false,
            extendHeadersWithSession: false,
            headers: { Authorization: `Basic ${authToken}` },
            data: { contractCode: contractNumber, language: INIT_UI_LANGUAGE },
          });

          const { data } = response;

          if (data?.payload?.numberOfActivePhones === 0) {
            dispatch(
              showModal(MODAL_TYPES.PHONE, {
                contractNumber,
                onModalConfirm({ phone }) {
                  dispatch(hideModal());

                  searchParams.append('phone', phone);
                  history.push({
                    pathname: '/profile/authorization/phone-activation',
                    search: searchParams.toString(),
                  });
                },
                onModalBack() {
                  dispatch(hideModal());
                  resolveSuccess(user);
                },
              }),
            );

            return;
          }

          await resolveSuccess(user);
        } catch (error) {
          console.error(error);
          dispatch(backToPageThatCausedAuth());
        } finally {
          dispatch(checkLoginData());
        }
      };

      if (ottCustomerId) {
        innerAnalyticsVolia.postEvent({
          eventGroup: 'external authorization screen',
          eventName: 'authorization request',
          eventValue: 'success',
        });

        dispatch(showLoader());
        await dispatch(loginUser({ code: ottCustomerId, loginType: 'code', errorModal, onSuccess, withLoader: false }));
        dispatch(hideLoader());
      } else if (errorCode === EXPIRED_CODE) {
        innerAnalyticsVolia.postEvent({
          eventGroup: 'external authorization screen',
          eventName: 'authorization request',
          eventValue: 'expired_code',
        });
        setIsCodeExpired(true);
        clearInterval(intervalRef.current);
      }
    };

    // first immidiately call
    smartTvLogin();
    // interval
    intervalRef.current = setInterval(smartTvLogin, QRCodeConfig.interval * 1000);

    return () => clearInterval(intervalRef.current);
    //SMRT-285, STVWL-552 TODO-Inna Почему-то компонент ререндерится каждую секунду,
    // если в зависимости передавать resolveSuccess
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [QRCodeConfig, dispatch, history, isCodeExpired, language]);

  useEffect(() => {
    if (!timer) setIsCodeExpired(true);
  }, [timer]);

  useEffect(() => {
    requestQRCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <StyledQRTitle>{i18n.t(`${i18nScope}.title`)}</StyledQRTitle>
      <StyledQRBlock>
        <QRDescription>
          {i18n.t(`${i18nScope}.QRDescription_line1`)}
          <br />
          {i18n.t(`${i18nScope}.QRDescription_line2`)}
        </QRDescription>
        {!isCodeExpired ? (
          <QRCodeWrapper>
            {!isEmpty(QRCodeConfig) && <QRCode value={QRCodeConfig.verificationUrlComplete} size={scaleSize(300)} />}
          </QRCodeWrapper>
        ) : (
          <RetryBlock />
        )}
        <QRDescription>{i18n.t(`${i18nScope}.QROr`)}</QRDescription>
        <QRDescription>{i18n.t(`${i18nScope}.QRAlternate`)}</QRDescription>
        <QRLink>tv.lifecell.ua/code</QRLink>
      </StyledQRBlock>
      <StyledCodeBlock>
        <CodeTitle>{i18n.t(`${i18nScope}.code`)}</CodeTitle>
        <UserCode isCodeExpired={isCodeExpired}>
          {formatCode(!isCodeExpired && QRCodeConfig.userCode ? QRCodeConfig.userCode : '——————')}
        </UserCode>
        <StyledActionSection sectionName={SECTION_NAME} defaultSection>
          <StyledButton
            label={i18n.t(`${i18nScope}.renewCode`)}
            onEnter={isCodeExpired ? () => requestQRCode({ fromButtonClick: true }) : noop}
            $disabled={!isCodeExpired}
          />
        </StyledActionSection>
        <StyledTimer>{!isCodeExpired && timeFormatter(timer)}</StyledTimer>
      </StyledCodeBlock>
    </>
  );
};

export default AuthorizationLoginQRPage;
